import React, { useId, useMemo, useState } from 'react';
import {
  DndContext,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  Route,
  Flag,
  CircleDot,
  Clock3,
  CheckCircle2,
  PauseCircle,
  Filter,
  Search,
  LayoutGrid,
  CalendarDays,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Target,
  Milestone,
  Link2,
  Github,
  RefreshCw,
  ExternalLink,
  Plus,
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  type Horizon,
  type MilestoneItem,
  type Priority,
  type RoadmapItem,
  type RoadmapItemInput,
  type RoadmapStatus,
  useDeleteRoadmapItemMutation,
  useRoadmapQuery,
  useSaveRoadmapItemMutation,
  useSyncRoadmapGithubMutation,
} from '@/client/api/roadmap/roadmap';
import { Label } from '@/components/ui/label';

type ViewMode = 'board' | 'calendar' | 'insights' | 'integrations';
type CalRange = 'day' | 'week' | 'month' | 'quarter' | 'year';

const STATUS_META: Record<
  RoadmapStatus,
  { label: string; icon: React.ReactNode; chip: string; bar: string; chart: string }
> = {
  shipped: {
    label: 'Shipped',
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    chip: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
    bar: 'bg-emerald-500',
    chart: '#10b981',
  },
  building: {
    label: 'Building',
    icon: <CircleDot className="h-3.5 w-3.5" />,
    chip: 'bg-sky-50 text-sky-700 ring-sky-200',
    bar: 'bg-sky-500',
    chart: '#0ea5e9',
  },
  planned: {
    label: 'Planned',
    icon: <Clock3 className="h-3.5 w-3.5" />,
    chip: 'bg-amber-50 text-amber-800 ring-amber-200',
    bar: 'bg-amber-500',
    chart: '#f59e0b',
  },
  parked: {
    label: 'Parked',
    icon: <PauseCircle className="h-3.5 w-3.5" />,
    chip: 'bg-gray-100 text-gray-600 ring-gray-200',
    bar: 'bg-gray-400',
    chart: '#9ca3af',
  },
};

const PRIORITY_CHIP: Record<Priority, string> = {
  P0: 'bg-rose-50 text-rose-700 ring-rose-200',
  P1: 'bg-orange-50 text-orange-700 ring-orange-200',
  P2: 'bg-slate-50 text-slate-600 ring-slate-200',
};

const HORIZON_LABEL: Record<Horizon, string> = {
  now: 'Now',
  next: 'Next',
  later: 'Later',
};

const VIEWS: { id: ViewMode; label: string; icon: React.ReactNode }[] = [
  { id: 'board', label: 'Board', icon: <LayoutGrid className="h-4 w-4" /> },
  { id: 'calendar', label: 'Calendar', icon: <CalendarDays className="h-4 w-4" /> },
  { id: 'insights', label: 'Insights', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'integrations', label: 'Integrations', icon: <Github className="h-4 w-4" /> },
];

const CAL_RANGES: { id: CalRange; label: string }[] = [
  { id: 'day', label: 'Day' },
  { id: 'week', label: 'Week' },
  { id: 'month', label: 'Month' },
  { id: 'quarter', label: 'Quarter' },
  { id: 'year', label: 'Year' },
];

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseISODate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toISOKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatShort(iso: string): string {
  return parseISODate(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function startOfWeek(d: Date): Date {
  const day = startOfDay(d);
  const pad = (day.getDay() + 6) % 7; // Monday-first
  day.setDate(day.getDate() - pad);
  return day;
}

function endOfWeek(d: Date): Date {
  const s = startOfWeek(d);
  return new Date(s.getFullYear(), s.getMonth(), s.getDate() + 6);
}

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function startOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), q, 1);
}

function endOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3) * 3;
  return new Date(d.getFullYear(), q + 3, 0);
}

function startOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 0, 1);
}

function endOfYear(d: Date): Date {
  return new Date(d.getFullYear(), 11, 31);
}

function rangeBounds(anchor: Date, range: CalRange): { start: Date; end: Date } {
  switch (range) {
    case 'day':
      return { start: startOfDay(anchor), end: startOfDay(anchor) };
    case 'week':
      return { start: startOfWeek(anchor), end: endOfWeek(anchor) };
    case 'month':
      return { start: startOfMonth(anchor), end: endOfMonth(anchor) };
    case 'quarter':
      return { start: startOfQuarter(anchor), end: endOfQuarter(anchor) };
    case 'year':
      return { start: startOfYear(anchor), end: endOfYear(anchor) };
  }
}

function shiftAnchor(anchor: Date, range: CalRange, delta: number): Date {
  const d = new Date(anchor);
  switch (range) {
    case 'day':
      d.setDate(d.getDate() + delta);
      break;
    case 'week':
      d.setDate(d.getDate() + delta * 7);
      break;
    case 'month':
      d.setMonth(d.getMonth() + delta);
      break;
    case 'quarter':
      d.setMonth(d.getMonth() + delta * 3);
      break;
    case 'year':
      d.setFullYear(d.getFullYear() + delta);
      break;
  }
  return d;
}

function rangeLabel(anchor: Date, range: CalRange): string {
  const { start, end } = rangeBounds(anchor, range);
  switch (range) {
    case 'day':
      return start.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    case 'week': {
      const a = start.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      const b = end.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
      return `${a} – ${b}`;
    }
    case 'month':
      return start.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    case 'quarter': {
      const q = Math.floor(start.getMonth() / 3) + 1;
      return `Q${q} ${start.getFullYear()}`;
    }
    case 'year':
      return String(start.getFullYear());
  }
}

function buildMonthGrid(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const startPad = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function dateInInclusive(d: Date, start: Date, end: Date): boolean {
  const t = startOfDay(d).getTime();
  return t >= startOfDay(start).getTime() && t <= startOfDay(end).getTime();
}

function EventChip({ item }: { item: RoadmapItem }) {
  return (
    <div
      className="truncate rounded px-1.5 py-0.5 text-[10px] font-medium text-white"
      style={{ backgroundColor: STATUS_META[item.status].chart }}
      title={item.title}
    >
      {item.title}
    </div>
  );
}

function MilestoneChip({ title }: { title: string }) {
  return (
    <div
      className="truncate rounded bg-teal-100/80 px-1.5 py-0.5 text-[10px] font-medium text-teal-900"
      title={title}
    >
      {title}
    </div>
  );
}

function HorizonColumn({ horizon, children }: { horizon: Horizon; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: horizon });
  return (
    <section
      ref={setNodeRef}
      className={`flex min-h-[280px] flex-col rounded-xl border bg-gray-50/80 transition ${
        isOver ? 'border-teal-300 bg-teal-50/60' : 'border-gray-100'
      }`}
    >
      {children}
    </section>
  );
}

function DraggableCard({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, attributes, listeners, transform, isDragging } = useDraggable({ id });
  return (
    <li
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined}
      className={`list-none cursor-grab touch-none ${isDragging ? 'relative z-10 opacity-80 shadow-lg' : ''}`}
    >
      {children}
    </li>
  );
}

function InitiativeCard({
  item,
  subItems = [],
  onAddSubItem,
  onEdit,
  compact = false,
}: {
  item: RoadmapItem;
  subItems?: RoadmapItem[];
  onAddSubItem?: () => void;
  onEdit?: (item: RoadmapItem) => void;
  compact?: boolean;
}) {
  const meta = STATUS_META[item.status];
  return (
    <div
      className={`rounded-lg border border-gray-100 bg-white shadow-sm transition hover:border-gray-200 ${
        compact ? 'p-2.5' : 'p-3.5'
      }`}
    >
      <div className="mb-2 flex items-start justify-between gap-2">
        <h3
          className={`font-semibold leading-snug text-gray-900 ${compact ? 'text-xs' : 'text-sm'}`}
        >
          {onEdit ? (
            <button
              type="button"
              onClick={() => onEdit(item)}
              className="text-left hover:text-teal-700 hover:underline"
            >
              {item.title}
            </button>
          ) : (
            item.title
          )}
        </h3>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${meta.chip}`}
        >
          {meta.icon}
          {meta.label}
        </span>
      </div>
      {item.summary && (
        <p className={`mb-3 leading-relaxed text-gray-500 ${compact ? 'text-[11px]' : 'text-xs'}`}>
          {item.summary}
        </p>
      )}
      {typeof item.progress === 'number' && (
        <div className="mb-3">
          <div className="mb-1 flex justify-between text-[11px] text-gray-500">
            <span>Progress</span>
            <span className="tabular-nums">{item.progress}%</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div className={`h-full rounded-full ${meta.bar}`} style={{ width: `${item.progress}%` }} />
          </div>
        </div>
      )}
      {item.blockedBy && (
        <div className="mb-2 flex items-start gap-1.5 rounded-md bg-rose-50 px-2 py-1.5 text-[11px] text-rose-700">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>{item.blockedBy}</span>
        </div>
      )}
      {item.dependsOn && item.dependsOn.length > 0 && (
        <div className="mb-2 flex items-start gap-1.5 text-[11px] text-gray-500">
          <Link2 className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
          <span>Depends on {item.dependsOn.join(', ')}</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-500">
        <span className={`rounded-md px-1.5 py-0.5 font-medium ring-1 ring-inset ${PRIORITY_CHIP[item.priority]}`}>
          {item.priority}
        </span>
        <span className="inline-flex items-center gap-1 rounded-md bg-gray-50 px-1.5 py-0.5 ring-1 ring-gray-100">
          <Flag className="h-3 w-3 text-gray-400" />
          {item.area}
        </span>
        {item.points > 0 && <span>{item.points} pts</span>}
        {item.targetDate && <span>{formatShort(item.targetDate)}</span>}
        <span className="text-gray-400">{item.quarter}</span>
        {item.owner && <span>{item.owner}</span>}
        {(item.githubIssues ?? []).map((gh) => (
          <a
            key={gh.ref}
            href={gh.url || `https://github.com/${gh.ref.replace('#', '/issues/')}`}
            target="_blank"
            rel="noreferrer"
            title={gh.error ? `${gh.ref}: ${gh.error}` : gh.title || gh.ref}
            onPointerDown={(e) => e.stopPropagation()}
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium ${
              gh.error
                ? 'bg-rose-50 text-rose-700 ring-1 ring-rose-200'
                : gh.state === 'closed'
                  ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                  : 'bg-gray-900 text-white'
            }`}
          >
            <Github className="h-3 w-3" />
            {gh.ref.split('/').pop()}
          </a>
        ))}
      </div>

      {!compact && (subItems.length > 0 || onAddSubItem) && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Sub-items · {subItems.length}
            </p>
            {onAddSubItem && (
              <button
                type="button"
                onClick={onAddSubItem}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium text-teal-700 hover:bg-teal-50"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
            )}
          </div>
          {subItems.length === 0 ? (
            <p className="rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-2 py-2 text-[11px] text-gray-400">
              No sub-items yet
            </p>
          ) : (
            <ul className="space-y-2 border-l-2 border-teal-100 pl-2.5">
              {subItems.map((child) => (
                <li key={child.id}>
                  <InitiativeCard item={child} compact onEdit={onEdit} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

type ItemFormDraft = {
  title: string;
  summary: string;
  area: string;
  status: RoadmapStatus;
  horizon: Horizon;
  quarter: string;
  owner: string;
  priority: Priority;
  points: string;
  targetDate: string;
  progress: string;
  githubIssues: string;
};

const EMPTY_DRAFT: ItemFormDraft = {
  title: '',
  summary: '',
  area: 'Product',
  status: 'planned',
  horizon: 'now',
  quarter: 'Q4 2026',
  owner: '',
  priority: 'P1',
  points: '3',
  targetDate: '',
  progress: '',
  githubIssues: '',
};

type ItemFormState =
  | { open: false }
  | { open: true; mode: 'root' }
  | { open: true; mode: 'edit'; item: RoadmapItem }
  | {
      open: true;
      mode: 'sub';
      parentId: string;
      parentTitle: string;
      defaults: Pick<RoadmapItem, 'area' | 'owner' | 'horizon' | 'quarter'>;
    };

function RoadmapItemFormDialog({
  state,
  areaOptions,
  onClose,
  onSubmit,
  onDelete,
}: {
  state: ItemFormState;
  areaOptions: string[];
  onClose: () => void;
  onSubmit: (item: RoadmapItemInput) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const formId = useId();
  const [draft, setDraft] = useState<ItemFormDraft>(EMPTY_DRAFT);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const open = state.open;
  const isSub = open && state.mode === 'sub';
  const editing = open && state.mode === 'edit' ? state.item : null;

  React.useEffect(() => {
    if (!state.open) return;
    setError(null);
    if (state.mode === 'edit') {
      const it = state.item;
      setDraft({
        title: it.title,
        summary: it.summary,
        area: it.area,
        status: it.status,
        horizon: it.horizon,
        quarter: it.quarter,
        owner: it.owner,
        priority: it.priority,
        points: String(it.points ?? 0),
        targetDate: it.targetDate ?? '',
        progress: typeof it.progress === 'number' ? String(it.progress) : '',
        githubIssues: (it.githubIssues ?? []).map((i) => i.ref).join('\n'),
      });
      return;
    }
    if (state.mode === 'sub') {
      setDraft({
        ...EMPTY_DRAFT,
        area: state.defaults.area,
        owner: state.defaults.owner,
        horizon: state.defaults.horizon,
        quarter: state.defaults.quarter,
        status: 'planned',
      });
      return;
    }
    setDraft({
      ...EMPTY_DRAFT,
      area: areaOptions[0] ?? 'Product',
    });
  }, [state, areaOptions]);

  const set =
    (key: keyof ItemFormDraft) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      setDraft((d) => ({ ...d, [key]: e.target.value }));
    };

  const run = async (fn: () => Promise<void>) => {
    setSaving(true);
    setError(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const title = draft.title.trim();
    const summary = draft.summary.trim();
    if (!title) {
      setError('Title is required');
      return;
    }
    const points = Number(draft.points);
    if (!Number.isFinite(points) || points < 0) {
      setError('Points must be a non-negative number');
      return;
    }
    let progress: number | undefined;
    if (draft.progress.trim()) {
      progress = Number(draft.progress);
      if (!Number.isFinite(progress) || progress < 0 || progress > 100) {
        setError('Progress must be 0–100');
        return;
      }
    }

    void run(() =>
      onSubmit({
        ...(editing ? { id: editing.id } : {}),
        title,
        summary,
        area: draft.area.trim() || 'Product',
        status: draft.status,
        horizon: draft.horizon,
        quarter: draft.quarter.trim() || 'TBD',
        owner: draft.owner.trim(),
        priority: draft.priority,
        points,
        targetDate: draft.targetDate.trim() || null,
        githubIssues: draft.githubIssues.split(/[\s,]+/).filter(Boolean),
        // null clears progress on an edit; omitted on create
        ...(progress !== undefined ? { progress } : editing ? { progress: null } : {}),
        ...(isSub && state.mode === 'sub' ? { parentId: state.parentId } : {}),
      }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit initiative' : isSub ? 'Add sub-item' : 'Add initiative'}</DialogTitle>
          <DialogDescription>
            {editing
              ? 'Changes are saved for everyone with Features access.'
              : isSub && state.mode === 'sub'
                ? `Nested under “${state.parentTitle}”.`
                : 'Adds a top-level card to the board.'}
          </DialogDescription>
        </DialogHeader>
        <form id={formId} onSubmit={handleSubmit} className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor={`${formId}-title`}>Title</Label>
            <Input
              id={`${formId}-title`}
              value={draft.title}
              onChange={set('title')}
              placeholder="Short initiative name"
              autoFocus
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${formId}-summary`}>Summary</Label>
            <textarea
              id={`${formId}-summary`}
              value={draft.summary}
              onChange={set('summary')}
              rows={3}
              placeholder="What this delivers"
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-area`}>Area</Label>
              <Input
                id={`${formId}-area`}
                list={`${formId}-areas`}
                value={draft.area}
                onChange={set('area')}
              />
              <datalist id={`${formId}-areas`}>
                {areaOptions.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-owner`}>Owner</Label>
              <Input id={`${formId}-owner`} value={draft.owner} onChange={set('owner')} />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-status`}>Status</Label>
              <select
                id={`${formId}-status`}
                value={draft.status}
                onChange={set('status')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {(Object.keys(STATUS_META) as RoadmapStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_META[s].label}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-horizon`}>Horizon</Label>
              <select
                id={`${formId}-horizon`}
                value={draft.horizon}
                onChange={set('horizon')}
                disabled={isSub}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-60"
              >
                {(Object.keys(HORIZON_LABEL) as Horizon[]).map((h) => (
                  <option key={h} value={h}>
                    {HORIZON_LABEL[h]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-priority`}>Priority</Label>
              <select
                id={`${formId}-priority`}
                value={draft.priority}
                onChange={set('priority')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {(['P0', 'P1', 'P2'] as Priority[]).map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-points`}>Points</Label>
              <Input
                id={`${formId}-points`}
                type="number"
                min={0}
                value={draft.points}
                onChange={set('points')}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-progress`}>Progress %</Label>
              <Input
                id={`${formId}-progress`}
                type="number"
                min={0}
                max={100}
                value={draft.progress}
                onChange={set('progress')}
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-quarter`}>Quarter</Label>
              <Input
                id={`${formId}-quarter`}
                value={draft.quarter}
                onChange={set('quarter')}
                placeholder="Q3 2026"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor={`${formId}-date`}>Target date</Label>
              <Input
                id={`${formId}-date`}
                type="date"
                value={draft.targetDate}
                onChange={set('targetDate')}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor={`${formId}-github`}>GitHub issues</Label>
            <textarea
              id={`${formId}-github`}
              value={draft.githubIssues}
              onChange={set('githubIssues')}
              rows={2}
              placeholder={'https://github.com/FeelyFeely/ff-api/issues/123\nff-admin#45'}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            />
            <p className="text-xs text-gray-400">
              One per line. Sync sets progress from closed issues; all closed marks it Shipped.
            </p>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </form>
        <DialogFooter className="gap-2 sm:justify-between">
          {editing ? (
            <Button
              type="button"
              variant="outline"
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
              disabled={saving}
              onClick={() => {
                if (window.confirm(`Delete “${editing.title}” and its sub-items?`)) {
                  void run(() => onDelete(editing.id));
                }
              }}
            >
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" form={formId} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save' : isSub ? 'Add sub-item' : 'Add initiative'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FeaturesRoadmapPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Features', href: '/admin/features/flags' },
    { label: 'Roadmap' },
  ]);

  const roadmap = useRoadmapQuery();
  const items = useMemo(() => roadmap.data?.items ?? [], [roadmap.data]);
  const milestones = useMemo(() => roadmap.data?.milestones ?? [], [roadmap.data]);
  const saveItem = useSaveRoadmapItemMutation();
  const deleteItem = useDeleteRoadmapItemMutation();
  const syncGithub = useSyncRoadmapGithubMutation();
  const [formState, setFormState] = useState<ItemFormState>({ open: false });
  const [view, setView] = useState<ViewMode>('board');
  const [calRange, setCalRange] = useState<CalRange>('month');
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<RoadmapStatus | 'all'>('all');
  const [areaFilter, setAreaFilter] = useState<string>('all');
  const [anchor, setAnchor] = useState(() => new Date());
  // Horizon moves shown before the save round-trips, so a dropped card doesn't snap back.
  const [moves, setMoves] = useState<Record<string, Horizon>>({});
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const areas = useMemo(
    () => Array.from(new Set(items.map((i) => i.area))).sort(),
    [items],
  );

  const childrenByParent = useMemo(() => {
    const map: Record<string, RoadmapItem[]> = {};
    for (const item of items) {
      if (!item.parentId) continue;
      (map[item.parentId] ??= []).push(item);
    }
    return map;
  }, [items]);

  const matchesFilters = (item: RoadmapItem, term: string) => {
    if (statusFilter !== 'all' && item.status !== statusFilter) return false;
    if (areaFilter !== 'all' && item.area !== areaFilter) return false;
    if (!term) return true;
    return (
      item.title.toLowerCase().includes(term) ||
      item.summary.toLowerCase().includes(term) ||
      item.area.toLowerCase().includes(term)
    );
  };

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    return items.filter((item) => matchesFilters(item, term));
  }, [items, query, statusFilter, areaFilter]);

  /** Board shows roots only; keep a root if it or any of its children match filters. */
  const filteredRoots = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filteredIds = new Set(filtered.map((i) => i.id));
    return items.filter((item) => {
      if (item.parentId) return false;
      if (filteredIds.has(item.id)) return true;
      const kids = childrenByParent[item.id] ?? [];
      return kids.some((c) => matchesFilters(c, term));
    });
  }, [items, filtered, childrenByParent, query, statusFilter, areaFilter]);

  const byHorizon = useMemo(() => {
    const buckets: Record<Horizon, RoadmapItem[]> = { now: [], next: [], later: [] };
    for (const item of filteredRoots) buckets[moves[item.id] ?? item.horizon].push(item);
    return buckets;
  }, [filteredRoots, moves]);

  const counts = useMemo(() => {
    const base = { shipped: 0, building: 0, planned: 0, parked: 0 };
    for (const item of items) {
      if (item.parentId) continue;
      base[item.status] += 1;
    }
    return base;
  }, [items]);

  const statusPie = useMemo(
    () =>
      (Object.keys(STATUS_META) as RoadmapStatus[]).map((status) => ({
        name: STATUS_META[status].label,
        value: counts[status],
        fill: STATUS_META[status].chart,
      })),
    [counts],
  );

  const areaBars = useMemo(() => {
    const byArea: Record<string, { area: string; points: number; items: number }> = {};
    for (const item of filtered) {
      const row = byArea[item.area] ?? { area: item.area, points: 0, items: 0 };
      row.points += item.points;
      row.items += 1;
      byArea[item.area] = row;
    }
    return Object.values(byArea);
  }, [filtered]);

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => rangeBounds(anchor, calRange),
    [anchor, calRange],
  );

  const calendarCells = useMemo(
    () => buildMonthGrid(anchor.getFullYear(), anchor.getMonth()),
    [anchor],
  );

  const weekDays = useMemo(() => {
    const start = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [anchor]);

  const quarterMonths = useMemo(() => {
    const start = startOfQuarter(anchor);
    return [0, 1, 2].map((i) => new Date(start.getFullYear(), start.getMonth() + i, 1));
  }, [anchor]);

  const yearMonths = useMemo(
    () => Array.from({ length: 12 }, (_, m) => new Date(anchor.getFullYear(), m, 1)),
    [anchor],
  );

  const itemsByDay = useMemo(() => {
    const byDay: Record<string, RoadmapItem[]> = {};
    for (const item of filtered) {
      if (!item.targetDate) continue;
      const d = parseISODate(item.targetDate);
      if (d.getFullYear() !== anchor.getFullYear() || d.getMonth() !== anchor.getMonth()) continue;
      (byDay[item.targetDate] ??= []).push(item);
    }
    return byDay;
  }, [filtered, anchor]);

  const itemsByDayAll = useMemo(() => {
    const byDay: Record<string, RoadmapItem[]> = {};
    for (const item of filtered) {
      if (!item.targetDate) continue;
      (byDay[item.targetDate] ??= []).push(item);
    }
    return byDay;
  }, [filtered]);

  const milestonesInRange = useMemo(
    () =>
      milestones.filter((m) =>
        dateInInclusive(parseISODate(m.date), rangeStart, rangeEnd),
      ),
    [milestones, rangeStart, rangeEnd],
  );

  const itemsInRange = useMemo(
    () =>
      filtered.filter(
        (i) => i.targetDate && dateInInclusive(parseISODate(i.targetDate), rangeStart, rangeEnd),
      ),
    [filtered, rangeStart, rangeEnd],
  );

  const today = useMemo(() => new Date(), []);
  const blockers = useMemo(() => filtered.filter((i) => i.blockedBy), [filtered]);
  const byQuarter = useMemo(() => {
    const rows: Record<string, { quarter: string; planned: number; building: number; shipped: number; parked: number; sort: string }> = {};
    for (const item of filtered) {
      if (item.parentId) continue;
      const [q, y] = item.quarter.split(' ');
      const row = (rows[item.quarter] ??= { quarter: item.quarter, planned: 0, building: 0, shipped: 0, parked: 0, sort: `${y ?? ''}${q ?? ''}` });
      row[item.status] += 1;
    }
    return Object.values(rows).sort((a, b) => a.sort.localeCompare(b.sort));
  }, [filtered]);

  const goToday = () => setAnchor(new Date());
  const shift = (delta: number) => setAnchor((a) => shiftAnchor(a, calRange, delta));

  const itemsForDate = (d: Date) => itemsByDayAll[toISOKey(d)] ?? [];
  const milestonesForDate = (d: Date) =>
    milestones.filter((m) => m.date === toISOKey(d));
  const itemsForMonth = (year: number, month: number) =>
    filtered.filter((i) => {
      if (!i.targetDate) return false;
      const d = parseISODate(i.targetDate);
      return d.getFullYear() === year && d.getMonth() === month;
    });

  const visibleChildrenFor = (parent: RoadmapItem) => {
    const term = query.trim().toLowerCase();
    const kids = childrenByParent[parent.id] ?? [];
    const parentMatches = matchesFilters(parent, term);
    if (parentMatches && !term && statusFilter === 'all' && areaFilter === 'all') return kids;
    const matchedKids = kids.filter((c) => matchesFilters(c, term));
    if (matchedKids.length > 0) return matchedKids;
    return parentMatches ? kids : [];
  };

  const submitItem = async (payload: RoadmapItemInput) => {
    await saveItem.mutateAsync(payload);
  };

  const removeItem = async (id: string) => {
    await deleteItem.mutateAsync(id);
  };

  const editItem = (item: RoadmapItem) => setFormState({ open: true, mode: 'edit', item });

  const linkedIssues = items.flatMap((item) => (item.githubIssues ?? []).map((gh) => ({ item, gh })));
  const lastGithubSync = items.reduce<string | null>(
    (latest, i) => (i.githubSyncedAt && (!latest || i.githubSyncedAt > latest) ? i.githubSyncedAt : latest),
    null,
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    const id = String(active.id);
    const to = over?.id as Horizon | undefined;
    const item = items.find((i) => i.id === id);
    if (!item || !to || (moves[id] ?? item.horizon) === to) return;
    setMoves((m) => ({ ...m, [id]: to }));
    saveItem.mutate(
      { id, horizon: to },
      {
        onSettled: () =>
          setMoves(({ [id]: _done, ...rest }) => rest),
      },
    );
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Route className="h-6 w-6 text-teal-600" />
            Roadmap
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Board, calendar and insights for the product roadmap. Changes save for everyone.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition ${
                  view === v.id
                    ? 'bg-teal-600 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v.icon}
                {v.label}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            className="bg-teal-600 hover:bg-teal-700"
            onClick={() => setFormState({ open: true, mode: 'root' })}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Add initiative
          </Button>
          {roadmap.isError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700">
              Could not load the roadmap
            </div>
          )}
        </div>
      </div>

      <RoadmapItemFormDialog
        state={formState}
        areaOptions={areas}
        onClose={() => setFormState({ open: false })}
        onSubmit={submitItem}
        onDelete={removeItem}
      />

      {/* Milestone strip */}
      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5">
          <Milestone className="h-4 w-4 text-teal-600" />
          <h2 className="text-sm font-semibold text-gray-800">Milestones</h2>
        </div>
        <div className="flex gap-3 overflow-x-auto px-4 py-3">
          {milestones.map((m) => (
            <div
              key={m.id}
              className="min-w-[160px] shrink-0 rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                {m.kind} · {formatShort(m.date)}
              </p>
              <p className="mt-0.5 text-sm font-medium text-gray-800">{m.title}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {(Object.keys(STATUS_META) as RoadmapStatus[]).map((status) => {
          const meta = STATUS_META[status];
          return (
            <button
              key={status}
              type="button"
              onClick={() => setStatusFilter((s) => (s === status ? 'all' : status))}
              className={`rounded-xl border bg-white p-4 text-left shadow-sm transition ring-1 ring-transparent hover:border-gray-300 ${
                statusFilter === status ? 'border-teal-400 ring-teal-100' : 'border-gray-100'
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${meta.chip}`}
                >
                  {meta.icon}
                  {meta.label}
                </span>
                <span className="text-2xl font-semibold tabular-nums text-gray-900">
                  {counts[status]}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filters — shared across views */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-100 bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search initiatives…"
            className="h-9 w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 text-sm outline-none focus:border-teal-400 focus:bg-white focus:ring-2 focus:ring-teal-100"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <select
            value={areaFilter}
            onChange={(e) => setAreaFilter(e.target.value)}
            className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm text-gray-700 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
          >
            <option value="all">All areas</option>
            {areas.map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
          {(statusFilter !== 'all' || areaFilter !== 'all' || query) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStatusFilter('all');
                setAreaFilter('all');
                setQuery('');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* ── Board ── */}
      {view === 'board' && (
        <DndContext sensors={sensors} onDragEnd={onDragEnd}>
        <div className="grid gap-4 lg:grid-cols-3">
          {(Object.keys(HORIZON_LABEL) as Horizon[]).map((horizon) => (
            <HorizonColumn key={horizon} horizon={horizon}>
              <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                <h2 className="text-sm font-semibold text-gray-800">{HORIZON_LABEL[horizon]}</h2>
                <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                  {byHorizon[horizon].length}
                </span>
              </header>
              <ul className="flex flex-1 flex-col gap-3 p-3">
                {byHorizon[horizon].length === 0 ? (
                  <li className="flex flex-1 items-center justify-center rounded-lg border border-dashed border-gray-200 bg-white/60 px-3 py-8 text-center text-sm text-gray-400">
                    {roadmap.isLoading ? 'Loading…' : 'Nothing in this horizon'}
                  </li>
                ) : (
                  byHorizon[horizon].map((item) => (
                    <DraggableCard key={item.id} id={item.id}>
                      <InitiativeCard
                        item={item}
                        onEdit={editItem}
                        subItems={visibleChildrenFor(item)}
                        onAddSubItem={() =>
                          setFormState({
                            open: true,
                            mode: 'sub',
                            parentId: item.id,
                            parentTitle: item.title,
                            defaults: {
                              area: item.area,
                              owner: item.owner,
                              horizon: item.horizon,
                              quarter: item.quarter,
                            },
                          })
                        }
                      />
                    </DraggableCard>
                  ))
                )}
              </ul>
            </HorizonColumn>
          ))}
        </div>
        </DndContext>
      )}

      {/* ── Calendar ── */}
      {view === 'calendar' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => shift(-1)} aria-label="Previous period">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="min-w-[200px] text-center text-base font-semibold text-gray-900">
                {rangeLabel(anchor, calRange)}
              </h2>
              <Button variant="outline" size="icon" onClick={() => shift(1)} aria-label="Next period">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={goToday}>
                Today
              </Button>
            </div>
            <div className="inline-flex flex-wrap rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
              {CAL_RANGES.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setCalRange(r.id)}
                  className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                    calRange === r.id
                      ? 'bg-teal-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {itemsInRange.length} initiative{itemsInRange.length === 1 ? '' : 's'} ·{' '}
            {milestonesInRange.length} milestone{milestonesInRange.length === 1 ? '' : 's'} in this{' '}
            {calRange}
          </p>

          {/* Day agenda */}
          {calRange === 'day' && (
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="border-b border-gray-100 bg-gray-50 px-4 py-3">
                <p className="text-sm font-semibold text-gray-800">
                  {sameDay(anchor, today) ? 'Today' : 'Agenda'}
                </p>
              </div>
              <ul className="divide-y divide-gray-100">
                {milestonesForDate(anchor).map((m) => (
                  <li key={m.id} className="flex items-start gap-3 px-4 py-3">
                    <span className="mt-0.5 rounded-md bg-teal-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-teal-800 ring-1 ring-teal-100">
                      {m.kind}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{m.title}</p>
                      <p className="text-xs text-gray-500">Milestone · {formatShort(m.date)}</p>
                    </div>
                  </li>
                ))}
                {itemsForDate(anchor).map((item) => (
                  <li key={item.id} className="list-none px-4 py-3">
                    <InitiativeCard item={item} />
                  </li>
                ))}
                {milestonesForDate(anchor).length === 0 && itemsForDate(anchor).length === 0 && (
                  <li className="px-4 py-10 text-center text-sm text-gray-400">
                    Nothing scheduled on this day
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Week columns */}
          {calRange === 'week' && (
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
                {weekDays.map((d) => (
                  <div
                    key={toISOKey(d)}
                    className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {WEEKDAYS[(d.getDay() + 6) % 7]}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {weekDays.map((d) => {
                  const key = toISOKey(d);
                  const dayItems = itemsForDate(d);
                  const dayMs = milestonesForDate(d);
                  const isToday = sameDay(d, today);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setAnchor(d);
                        setCalRange('day');
                      }}
                      className={`min-h-[160px] border-b border-r border-gray-100 p-1.5 text-left transition hover:bg-gray-50 ${
                        isToday ? 'bg-teal-50/40' : 'bg-white'
                      }`}
                    >
                      <span
                        className={`mb-1 inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                          isToday ? 'bg-teal-600 text-white' : 'text-gray-600'
                        }`}
                      >
                        {d.getDate()}
                      </span>
                      <div className="space-y-1">
                        {dayMs.map((m) => (
                          <MilestoneChip key={m.id} title={m.title} />
                        ))}
                        {dayItems.slice(0, 4).map((item) => (
                          <EventChip key={item.id} item={item} />
                        ))}
                        {dayItems.length > 4 && (
                          <p className="px-1 text-[10px] text-gray-400">+{dayItems.length - 4}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month grid */}
          {calRange === 'month' && (
            <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
              <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
                {WEEKDAYS.map((d) => (
                  <div
                    key={d}
                    className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500"
                  >
                    {d}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {calendarCells.map((day, idx) => {
                  if (!day) {
                    return <div key={`pad-${idx}`} className="min-h-[110px] bg-gray-50/40" />;
                  }
                  const key = toISOKey(day);
                  const dayItems = itemsByDay[key] ?? [];
                  const dayMilestones = milestonesForDate(day);
                  const isToday = sameDay(day, today);
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setAnchor(day);
                        setCalRange('day');
                      }}
                      className={`min-h-[110px] border-b border-r border-gray-100 p-1.5 text-left transition hover:bg-gray-50 ${
                        isToday ? 'bg-teal-50/40' : 'bg-white'
                      }`}
                    >
                      <div className="mb-1 flex items-center justify-between">
                        <span
                          className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                            isToday ? 'bg-teal-600 text-white' : 'text-gray-600'
                          }`}
                        >
                          {day.getDate()}
                        </span>
                        {dayMilestones.length > 0 && (
                          <Target className="h-3.5 w-3.5 text-teal-600" />
                        )}
                      </div>
                      <div className="space-y-1">
                        {dayMilestones.map((m) => (
                          <MilestoneChip key={m.id} title={m.title} />
                        ))}
                        {dayItems.slice(0, 3).map((item) => (
                          <EventChip key={item.id} item={item} />
                        ))}
                        {dayItems.length > 3 && (
                          <p className="px-1 text-[10px] text-gray-400">
                            +{dayItems.length - 3} more
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Quarter — 3 months */}
          {calRange === 'quarter' && (
            <div className="grid gap-4 md:grid-cols-3">
              {quarterMonths.map((monthStart) => {
                const y = monthStart.getFullYear();
                const m = monthStart.getMonth();
                const monthItems = itemsForMonth(y, m);
                const monthMs = milestones.filter((ms) => {
                  const d = parseISODate(ms.date);
                  return d.getFullYear() === y && d.getMonth() === m;
                });
                const isCurrentMonth =
                  today.getFullYear() === y && today.getMonth() === m;
                return (
                  <section
                    key={`${y}-${m}`}
                    className={`flex min-h-[240px] flex-col rounded-xl border bg-white shadow-sm ${
                      isCurrentMonth ? 'border-teal-300' : 'border-gray-100'
                    }`}
                  >
                    <header className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                      <button
                        type="button"
                        className="text-sm font-semibold text-gray-800 hover:text-teal-700"
                        onClick={() => {
                          setAnchor(monthStart);
                          setCalRange('month');
                        }}
                      >
                        {MONTH_SHORT[m]} {y}
                      </button>
                      <span className="rounded-full bg-gray-50 px-2 py-0.5 text-xs text-gray-500 ring-1 ring-gray-200">
                        {monthItems.length}
                      </span>
                    </header>
                    <ul className="flex flex-1 flex-col gap-2 p-3">
                      {monthMs.map((ms) => (
                        <li key={ms.id}>
                          <MilestoneChip title={ms.title} />
                        </li>
                      ))}
                      {monthItems.map((item) => (
                        <li key={item.id}>
                          <EventChip item={item} />
                          <p className="mt-0.5 px-1 text-[10px] text-gray-400">
                            {item.targetDate ? formatShort(item.targetDate) : ''} · {item.owner}
                          </p>
                        </li>
                      ))}
                      {monthItems.length === 0 && monthMs.length === 0 && (
                        <li className="flex flex-1 items-center justify-center text-xs text-gray-400">
                          Empty
                        </li>
                      )}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}

          {/* Year — 12 months */}
          {calRange === 'year' && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {yearMonths.map((monthStart) => {
                const y = monthStart.getFullYear();
                const m = monthStart.getMonth();
                const monthItems = itemsForMonth(y, m);
                const shipped = monthItems.filter((i) => i.status === 'shipped').length;
                const building = monthItems.filter((i) => i.status === 'building').length;
                const isCurrentMonth =
                  today.getFullYear() === y && today.getMonth() === m;
                return (
                  <button
                    key={`${y}-${m}`}
                    type="button"
                    onClick={() => {
                      setAnchor(monthStart);
                      setCalRange('month');
                    }}
                    className={`rounded-xl border bg-white p-4 text-left shadow-sm transition hover:border-teal-300 ${
                      isCurrentMonth ? 'border-teal-400 ring-1 ring-teal-100' : 'border-gray-100'
                    }`}
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-900">{MONTH_SHORT[m]}</span>
                      <span className="text-lg font-semibold tabular-nums text-gray-800">
                        {monthItems.length}
                      </span>
                    </div>
                    <div className="mb-2 flex h-1.5 overflow-hidden rounded-full bg-gray-100">
                      {monthItems.length > 0 && (
                        <>
                          <div
                            className="bg-emerald-500"
                            style={{ width: `${(shipped / monthItems.length) * 100}%` }}
                          />
                          <div
                            className="bg-sky-500"
                            style={{ width: `${(building / monthItems.length) * 100}%` }}
                          />
                          <div
                            className="bg-amber-400"
                            style={{
                              width: `${((monthItems.length - shipped - building) / monthItems.length) * 100}%`,
                            }}
                          />
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500">
                      {shipped} shipped · {building} building
                    </p>
                    <div className="mt-2 space-y-1">
                      {monthItems.slice(0, 2).map((item) => (
                        <EventChip key={item.id} item={item} />
                      ))}
                      {monthItems.length > 2 && (
                        <p className="text-[10px] text-gray-400">+{monthItems.length - 2} more</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
            <h3 className="mb-3 text-sm font-semibold text-gray-800">No target date</h3>
            <ul className="flex flex-wrap gap-2">
              {filtered
                .filter((i) => !i.targetDate)
                .map((i) => (
                  <li
                    key={i.id}
                    className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600"
                  >
                    {i.title}
                    {i.blockedBy ? ' · blocked' : ''}
                  </li>
                ))}
              {filtered.every((i) => i.targetDate) && (
                <li className="text-sm text-gray-400">All filtered items have dates.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* ── Insights ── */}
      {view === 'insights' && (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-3">
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:col-span-1">
              <h3 className="mb-1 text-sm font-semibold text-gray-800">Status mix</h3>
              <p className="mb-3 text-xs text-gray-500">Top-level initiatives</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusPie}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={48}
                      outerRadius={78}
                      paddingAngle={3}
                    >
                      {statusPie.map((entry) => (
                        <Cell key={entry.name} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm lg:col-span-2">
              <h3 className="mb-1 text-sm font-semibold text-gray-800">By quarter</h3>
              <p className="mb-3 text-xs text-gray-500">Initiatives due each quarter, by status</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byQuarter}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="quarter" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    {(['shipped', 'building', 'planned', 'parked'] as RoadmapStatus[]).map((s) => (
                      <Bar key={s} dataKey={s} stackId="q" fill={STATUS_META[s].chart} name={STATUS_META[s].label} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <h3 className="mb-1 text-sm font-semibold text-gray-800">By area</h3>
              <p className="mb-3 text-xs text-gray-500">Initiatives in the current filter</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={areaBars}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="area" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="items" fill="#0d9488" name="Initiatives" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-rose-500" />
                <h3 className="text-sm font-semibold text-gray-800">Blockers & risks</h3>
              </div>
              {blockers.length === 0 ? (
                <p className="text-sm text-gray-400">No blockers in the current filter.</p>
              ) : (
                <ul className="space-y-2">
                  {blockers.map((b) => (
                    <li
                      key={b.id}
                      className="rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2"
                    >
                      <p className="text-sm font-medium text-gray-900">{b.title}</p>
                      <p className="mt-0.5 text-xs text-rose-700">{b.blockedBy}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center gap-2">
                <Target className="h-4 w-4 text-teal-600" />
                <h3 className="text-sm font-semibold text-gray-800">Upcoming milestones</h3>
              </div>
              <ul className="space-y-2">
                {milestones.filter((m) => parseISODate(m.date) >= startOfDay(today)).map((m) => (
                  <li
                    key={m.id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{m.title}</p>
                      <p className="text-[11px] uppercase tracking-wide text-gray-400">{m.kind}</p>
                    </div>
                    <span className="text-sm tabular-nums text-gray-600">{formatShort(m.date)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ── Integrations (GitHub Issues, one-way) ── */}
      {view === 'integrations' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-100 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-900 text-white">
                  <Github className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">GitHub Issues</h3>
                  <p className="mt-0.5 max-w-xl text-sm text-gray-500">
                    Link issues to an initiative from its edit form. Sync reads them from GitHub (nothing is
                    written back): progress is the share closed, all closed marks it Shipped, some closed marks
                    it Building.
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    {lastGithubSync
                      ? `Last sync ${new Date(lastGithubSync).toLocaleString(undefined, {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}`
                      : 'Not synced yet'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                disabled={syncGithub.isPending || linkedIssues.length === 0}
                onClick={() => syncGithub.mutate()}
              >
                <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${syncGithub.isPending ? 'animate-spin' : ''}`} />
                {syncGithub.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
            </div>
            {syncGithub.isError && (
              <p className="mt-3 text-sm text-rose-600">
                {syncGithub.error instanceof Error ? syncGithub.error.message : 'Sync failed'}
              </p>
            )}
            {syncGithub.data && (
              <p className="mt-3 text-sm text-gray-600">
                Read {syncGithub.data.issues} issues on {syncGithub.data.items} initiatives;{' '}
                {syncGithub.data.changed} changed.
                {syncGithub.data.errors.length > 0 && (
                  <span className="text-rose-600"> Problems: {syncGithub.data.errors.join(', ')}</span>
                )}
              </p>
            )}
          </div>

          <div className="rounded-xl border border-gray-100 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-800">Linked issues</h3>
              <p className="text-xs text-gray-500">
                {items.filter((i) => i.githubIssues?.length).length} of {items.length} initiatives linked
              </p>
            </div>
            {linkedIssues.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-400">
                No issues linked yet. Open an initiative and add issue links.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50 text-[11px] uppercase tracking-wide text-gray-400">
                      <th className="px-4 py-2.5 font-medium">Initiative</th>
                      <th className="px-4 py-2.5 font-medium">Issue</th>
                      <th className="px-4 py-2.5 font-medium">State</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linkedIssues.map(({ item, gh }) => (
                      <tr key={`${item.id}-${gh.ref}`} className="border-b border-gray-50 hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => editItem(item)}
                            className="text-left font-medium text-gray-900 hover:text-teal-700 hover:underline"
                          >
                            {item.title}
                          </button>
                          <p className="text-xs text-gray-500">{item.area}</p>
                        </td>
                        <td className="px-4 py-3">
                          <a
                            href={gh.url || `https://github.com/${gh.ref.replace('#', '/issues/')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 font-mono text-xs text-teal-700 hover:underline"
                          >
                            <Github className="h-3.5 w-3.5" />
                            {gh.ref}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                          {gh.title && <p className="mt-0.5 text-xs text-gray-500">{gh.title}</p>}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {gh.error ? (
                            <span className="text-rose-600">{gh.error}</span>
                          ) : (
                            <span className="capitalize text-gray-600">{gh.state ?? 'not synced'}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturesRoadmapPage;
